import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/Components/ui/button';

export const INK = 'hsl(164 33% 7%)';
export const DEMO_PATH = '/hirewithus/book-demo';
export const CONTACT_PATH = '/hirewithus/contact';

export function IndustryFlowStyles() {
  return <style>{FLOW_CSS}</style>;
}

export function Section({ children, muted = false, id, compact = false }) {
  return (
    <section id={id} className={`scroll-mt-24 ${compact ? 'py-10 lg:py-14' : 'py-12 lg:py-16'} ${muted ? 'bg-muted/30' : 'bg-background'}`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">{children}</div>
    </section>
  );
}

export function SectionIntro({ eyebrow, title, copy, fullWidth = true, titleClassName = 'text-3xl sm:text-4xl' }) {
  return (
    <div className={`mb-10 ${fullWidth ? 'max-w-none' : 'max-w-3xl'}`}>
      <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
        {eyebrow}
      </p>
      <h2 className={`${titleClassName} font-bold tracking-tight text-foreground`}>{title}</h2>
      <p className="mt-4 text-lg leading-relaxed text-muted-foreground">{copy}</p>
    </div>
  );
}

export function FlowConnector({ horizontal = false }) {
  return (
    <>
      <span className={`industry-flow-line mx-auto h-px w-6 shrink-0 ${horizontal ? 'block self-center' : 'hidden lg:block'}`} aria-hidden="true" />
      <span className={`industry-flow-line mx-auto h-6 w-px shrink-0 ${horizontal ? 'hidden' : 'block lg:hidden'}`} aria-hidden="true" />
    </>
  );
}

export function ProblemGrid({ items, highlighted = true }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {items.map((item) => (
        <article
          key={item.title}
          className={`group rounded-2xl border p-5 transition duration-200 hover:-translate-y-0.5 hover:shadow-md motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:hover:shadow-none ${
            highlighted
              ? 'border-primary bg-primary/5 hover:border-primary hover:bg-primary/5'
              : 'border-primary/30 bg-primary/5 hover:border-primary/45'
          }`}
        >
          <span className={`mb-4 flex h-11 w-11 items-center justify-center rounded-xl border transition-colors duration-200 ${
            highlighted
              ? 'border-primary/30 bg-primary/10 text-primary'
              : 'border-primary/20 bg-primary/10 text-primary group-hover:border-primary/30'
          }`}>
            <item.icon className="h-5 w-5" aria-hidden="true" />
          </span>
          <h3 className="text-base font-semibold text-foreground">{item.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.detail}</p>
        </article>
      ))}
    </div>
  );
}

export function IndustryWorkflow({ steps, plainDetail = false, uniformCards = false, hideDetail = false, showDetailMarker = true }) {
  const [active, setActive] = useState(0);
  const current = steps[active];

  return (
    <>
      <ol className={uniformCards ? 'flex overflow-x-auto pb-2 lg:overflow-visible lg:pb-0' : 'flex flex-col gap-3 lg:flex-row lg:items-stretch'}>
        {steps.map((step, index) => {
          const selected = index === active;
          return (
            <li
              key={step.title}
              className={uniformCards ? 'flex shrink-0 basis-56 flex-row items-stretch gap-3 lg:min-w-0 lg:flex-1' : 'flex min-w-0 flex-1 flex-col gap-3 lg:flex-row lg:items-center'}
            >
              <button
                type="button"
                aria-pressed={selected}
                onClick={() => setActive(index)}
                className={`group flex min-h-11 w-full flex-1 flex-col items-start rounded-2xl border px-4 py-4 text-left transition duration-200 hover:-translate-y-0.5 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:hover:shadow-none ${
                  uniformCards ? 'h-48 min-h-48' : ''
                } ${
                  selected
                    ? 'border-primary bg-primary/5'
                    : 'border-primary/30 bg-primary/5 hover:border-primary/45'
                }`}
              >
                <span
                  className={`mb-3 flex h-10 w-10 items-center justify-center rounded-xl border transition-colors duration-200 ${
                    selected
                      ? 'border-primary/30 bg-primary/10 text-primary'
                      : 'border-primary/20 bg-primary/10 text-primary group-hover:border-primary/30'
                  }`}
                >
                  <step.icon className="h-4 w-4" aria-hidden="true" />
                </span>
                <span className="text-sm font-semibold text-foreground">{step.title}</span>
              </button>
              {index < steps.length - 1 ? <FlowConnector horizontal={uniformCards} /> : uniformCards ? <span className="h-px w-6 shrink-0" aria-hidden="true" /> : null}
            </li>
          );
        })}
      </ol>
      {!hideDetail ? (
        <p
          className={plainDetail
            ? 'mt-6 text-base font-normal leading-relaxed text-foreground'
            : 'mt-6 rounded-2xl border border-primary/30 bg-primary/5 px-5 py-4 text-base leading-relaxed text-foreground'}
          aria-live="polite"
        >
          {plainDetail && showDetailMarker ? <span aria-hidden="true">* </span> : null}
          {!plainDetail ? <span className="font-semibold">{current.title}. </span> : null}
          {plainDetail ? `${current.title}. ${current.detail}` : current.detail}
        </p>
      ) : null}
    </>
  );
}

export function CapabilityGrid({ items, highlighted = true }) {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {items.map((item) => (
        <a
          key={item.title}
          href={item.href}
          className={`group flex min-h-11 flex-col rounded-2xl border p-5 transition duration-200 hover:-translate-y-0.5 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:hover:shadow-none ${
            highlighted
              ? 'border-primary bg-primary/5 hover:border-primary hover:bg-primary/5'
              : 'border-primary/30 bg-primary/5 hover:border-primary/45'
          }`}
        >
          <span className={`mb-4 flex h-11 w-11 items-center justify-center rounded-xl border transition-colors duration-200 ${
            highlighted
              ? 'border-primary/30 bg-primary/10 text-primary'
              : 'border-primary/20 bg-primary/10 text-primary group-hover:border-primary/30'
          }`}>
            <item.icon className="h-5 w-5" aria-hidden="true" />
          </span>
          <span className="text-base font-semibold text-foreground group-hover:text-primary">{item.title}</span>
          <span className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.helps}</span>
        </a>
      ))}
    </div>
  );
}

export function EvidenceBoard({
  profileMark,
  profileTitle,
  profileNote,
  signals,
  reviewLabel,
  nextLabel,
  signalMotion = false,
  singleCard = false,
  variant = 'default',
  connectedFlow = false,
}) {
  const isTechnical = variant === 'technical';
  const hasFlowConnectors = isTechnical || connectedFlow;
  if (singleCard) {
    return (
      <article className="mx-auto max-w-5xl overflow-hidden rounded-3xl border border-primary bg-primary/5">
        <header className="flex flex-col gap-3 border-b border-primary/20 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 text-sm font-semibold text-primary">
              {profileMark}
            </span>
            <span className="min-w-0">
              <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Example profile</span>
              <span className="mt-1 block text-base font-semibold text-foreground">{profileTitle}</span>
              <span className="mt-0.5 block text-sm text-muted-foreground">{profileNote}</span>
            </span>
          </div>
          <p className="text-xs font-semibold uppercase tracking-wider text-primary sm:text-right">{reviewLabel}</p>
        </header>
        <ul className="grid px-4 sm:grid-cols-2 sm:gap-x-12 sm:px-5" aria-label={reviewLabel}>
          {signals.map((item) => (
            <li key={item} className="flex min-h-11 items-center justify-between gap-4 border-b border-primary/15 py-2 last:border-b-0 sm:[&:nth-last-child(2):nth-child(odd)]:border-b-0">
              <span className="flex min-w-0 items-center gap-2.5">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
                <span className="text-sm font-medium text-foreground">{item}</span>
              </span>
              <span className="shrink-0 text-xs font-medium uppercase tracking-wide text-muted-foreground">Recorded</span>
            </li>
          ))}
        </ul>
        <p className="border-t border-primary/20 px-4 py-3 text-sm font-semibold text-foreground sm:px-5">{nextLabel}</p>
      </article>
    );
  }

  return (
    <div className={`grid ${hasFlowConnectors ? 'items-center gap-5 lg:grid-cols-[minmax(0,0.8fr)_2rem_minmax(0,0.85fr)_2rem_minmax(0,1.1fr)] lg:gap-6' : 'items-center gap-6 lg:grid-cols-[0.9fr_0.8fr_1fr]'}`}>
      <article className={`${isTechnical ? 'rounded-2xl p-4' : 'rounded-3xl p-5'} border border-primary/30 bg-primary/10`}>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Example profile</p>
        <div className={`${isTechnical ? 'mt-3' : 'mt-4'} flex items-center gap-3`}>
          <span className={`flex items-center justify-center bg-primary/10 text-sm font-semibold text-primary ${isTechnical ? 'h-10 w-10 rounded-xl' : 'h-12 w-12 rounded-2xl'}`}>
            {profileMark}
          </span>
          <span>
            <span className="block text-base font-semibold text-foreground">{profileTitle}</span>
            <span className="block text-sm text-muted-foreground">{profileNote}</span>
          </span>
        </div>
      </article>

      {hasFlowConnectors ? <FlowConnector /> : null}

      {hasFlowConnectors ? (
        <article className={`overflow-hidden border border-primary/30 bg-primary/5 px-4 ${isTechnical ? 'rounded-2xl' : 'rounded-3xl'}`}>
          <ul className="divide-y divide-primary/15">
            {signals.map((item) => (
              <li key={item} className="flex min-h-11 items-center py-2.5 text-sm font-medium text-foreground">
                {item}
              </li>
            ))}
          </ul>
        </article>
      ) : (
        <ul className="space-y-2">
          {signals.map((item) => (
            <li
              key={item}
              className="group flex items-center justify-between rounded-xl border border-primary/20 bg-background px-4 py-3 text-sm font-medium text-foreground"
            >
              {item}
              <ArrowRight className={`${signalMotion ? 'industry-signal-arrow ' : 'transition-transform duration-200 group-hover:translate-x-0.5 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0 '}h-4 w-4 text-primary`} aria-hidden="true" />
            </li>
          ))}
        </ul>
      )}

      {hasFlowConnectors ? <FlowConnector /> : null}

      <article className={`${isTechnical ? 'rounded-2xl p-4' : 'rounded-3xl p-5'} border border-primary bg-primary/5`}>
        <p className="text-xs font-semibold uppercase tracking-wider text-primary">{reviewLabel}</p>
        <ul className={`${isTechnical ? 'mt-3 divide-y divide-primary/15' : 'mt-4 space-y-3'}`}>
          {signals.map((item) => (
            <li key={item} className={`flex items-center justify-between text-sm ${isTechnical ? 'min-h-11 gap-4 py-2.5' : 'border-b border-border pb-2 last:border-0'}`}>
              <span className="text-foreground">{item}</span>
              <span className="text-muted-foreground">Recorded</span>
            </li>
          ))}
        </ul>
        <p className={`${isTechnical ? 'mt-3 border-t border-primary/20 pt-3' : 'mt-4'} text-sm font-semibold text-foreground`}>{nextLabel}</p>
      </article>
    </div>
  );
}

// Custom bottom-CTA treatment: keep independent from general information-card styling.
export function IndustryCta({
  title,
  titleLead,
  titleAccent,
  copy,
  primaryLabel,
  primaryHref,
  secondaryLabel,
  secondaryHref,
  compact = false,
  variant = 'emerald',
}) {
  const usesEmeraldVariant = variant === 'emerald';

  return (
    <section className={`bg-background ${compact ? 'py-12 lg:py-14' : 'py-16 lg:py-24'}`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div
          className={`relative overflow-hidden rounded-[2rem] px-6 text-center text-white sm:px-10 ${compact ? 'py-10 lg:py-12' : 'py-12 lg:py-16'} ${
            usesEmeraldVariant
              ? 'bg-gradient-to-br from-[hsl(164_33%_7%)] via-[hsl(162_56%_15%)] to-[hsl(164_42%_10%)]'
              : ''
          }`}
          style={usesEmeraldVariant ? undefined : { backgroundColor: INK }}
        >
          <div className="relative mx-auto max-w-5xl">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
              {titleLead && titleAccent ? (
                <>
                  <span className="text-white">{titleLead}</span>{' '}
                  <span className="text-emerald-300">{titleAccent}</span>
                </>
              ) : (
                <span className="bg-[linear-gradient(90deg,hsl(0_0%_100%)_0%,hsl(0_0%_100%)_52%,hsl(160_84%_78%)_52%,hsl(160_84%_78%)_100%)] bg-clip-text text-transparent">
                  {title}
                </span>
              )}
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-white/75">{copy}</p>
            <div className={`mt-8 ${secondaryLabel ? 'flex flex-col items-center justify-center gap-3 sm:flex-row' : ''}`}>
              <Button
                asChild
                size="lg"
                className="!h-12 !rounded-full !bg-white px-8 text-base font-semibold !text-foreground hover:!bg-white/90"
              >
                <a href={primaryHref}>
                  {primaryLabel}
                  <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
                </a>
              </Button>
              {secondaryLabel ? (
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="!h-12 !rounded-full border-white/40 bg-transparent px-8 text-base font-semibold !text-white hover:!bg-white/10"
                >
                  <a href={secondaryHref}>{secondaryLabel}</a>
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const FLOW_CSS = `
.industry-flow-line {
  background-image: linear-gradient(hsl(var(--primary)) 50%, transparent 50%);
  background-size: 8px 8px;
  animation: industry-flow 1.1s linear infinite;
}
.industry-flow-line.h-px {
  background-image: linear-gradient(90deg, hsl(var(--primary)) 50%, transparent 50%);
  background-size: 12px 1px;
}
.industry-signal-arrow {
  animation: industry-signal 1.6s ease-in-out infinite;
}
@media (prefers-reduced-motion: reduce) {
  .industry-flow-line { animation: none; background-image: none; background-color: hsl(var(--primary) / 0.45); }
  .industry-signal-arrow { animation: none; }
}
@keyframes industry-flow {
  from { background-position: 0 0; }
  to { background-position: 12px 12px; }
}
@keyframes industry-signal {
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(3px); }
}
`;
